import BaseTable from 'components/Tables/BaseTable';
import React, { useEffect, useMemo, useState } from 'react';
import { useTours } from 'features/Responder/features/Tours';
import { usePersonnel } from 'features/Responder/features/Personnel/contexts/PersonnelContext';

export const GeolocationHistory = ({ mode: initialMode = 'overall' }) => {
  const { setCenter } = useTours();
  const { geolocationHistory = {} } = usePersonnel();
  const [currentTime, setCurrentTime] = useState(Date.now());
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [mode, setMode] = useState(initialMode);

  // Update current time every second to refresh "time ago" calculations
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentTime(Date.now());
    }, 1000);

    return () => clearInterval(interval);
  }, []);

  // Helper function to calculate time ago
  const timeAgo = (timestamp) => {
    if (!timestamp) return '-';
    const now = currentTime;
    const time = new Date(timestamp);
    const diffInSeconds = Math.floor((now - time) / 1000);

    if (diffInSeconds < 10) return 'Live';
    if (diffInSeconds < 60) return `${diffInSeconds}s ago`;
    if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)}m ago`;
    if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)}h ago`;
    return `${Math.floor(diffInSeconds / 86400)}d ago`;
  };

  // Transform geolocation data for BaseTable
  const rows = useMemo(() => {
    if (mode === 'per-user') {
      // Return array of objects with userId keys and their entries
      const userGroups = [];
      Object.entries(geolocationHistory || {}).forEach(([userId, entries]) => {
        if (Array.isArray(entries) && entries.length > 0) {
          userGroups.push({
            [userId]: entries
              .map((entry, idx) => ({
                id: `${userId}-${idx}`,
                userId: entry.userId,
                timestamp: entry.timestamp,
                type: entry.type,
                ...entry, // Include any other fields
              }))
              .sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp)),
          });
        }
      });
      return userGroups;
    } else {
      // Return all entries flattened and sorted
      const allEntries = [];
      Object.entries(geolocationHistory || {}).forEach(([userId, entries]) => {
        if (Array.isArray(entries)) {
          entries.forEach((entry, idx) => {
            allEntries.push({
              id: `${userId}-${idx}`,
              userId: entry.userId,
              timestamp: entry.timestamp,
              type: entry.type,
              ...entry, // Include any other fields
            });
          });
        }
      });
      return allEntries.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
    }
  }, [geolocationHistory, mode]);

  // Table columns configuration for per-user mode
  const perUserColumns = useMemo(
    () => [
      {
        header: 'User ID',
        accessorKey: 'userId',
        size: 0,
        Cell: ({ cell }) => <div className="text-white fs-6">#{cell.getValue()}</div>,
      },
      {
        header: 'Time',
        accessorKey: 'timestamp',
        size: 0,
        Cell: ({ cell }) => (
          <div className="text-white text-nowrap fs-6">{timeAgo(cell.getValue())}</div>
        ),
      },
      {
        header: 'Type',
        accessorKey: 'type',
        size: 0,
        Cell: ({ cell }) => <div className="text-white fs-6">{cell.getValue() || '-'}</div>,
      },
    ],
    [currentTime],
  );

  // Table columns configuration for overall mode
  const overallColumns = useMemo(
    () => [
      {
        header: 'User ID',
        accessorKey: 'userId',
        size: 0,
        Cell: ({ cell }) => <div className="text-white fs-6">#{cell.getValue()}</div>,
      },
      {
        header: 'Time',
        accessorKey: 'timestamp',
        size: 0,
        Cell: ({ cell }) => (
          <div className="text-white text-nowrap fs-6">{timeAgo(cell.getValue())}</div>
        ),
      },
      {
        header: 'Type',
        accessorKey: 'type',
        size: 0,
        Cell: ({ cell }) => <div className="text-white fs-6">{cell.getValue() || '-'}</div>,
      },
    ],
    [currentTime],
  );

  const handleRowClick = (row) => {
    // Center map on the clicked location if coordinates are available
    if (row.lat && row.lng) {
      setCenter({
        lat: row.lat,
        lng: row.lng,
      });
    }
  };

  const toggleMode = () => {
    setMode(mode === 'overall' ? 'per-user' : 'overall');
    setSelectedUserId(null); // Clear selection when switching modes
  };

  return (
    <div
      className="p-3 rounded-2"
      style={{
        background: '#0b1826',
      }}
    >
      <div className="d-flex justify-content-between align-items-center mb-2">
        <h4>Event History</h4>
        <button
          className="btn btn-sm btn-outline-light"
          onClick={toggleMode}
          style={{ fontSize: '0.8em' }}
        >
          {mode === 'per-user' ? 'Per User Summary' : 'All Events'}
        </button>
      </div>
      {rows.length > 0 ? (
        mode === 'per-user' ? (
          <div>
            {rows.map((userGroup, idx) => {
              const userId = Object.keys(userGroup)[0];
              const entries = userGroup[userId];
              return (
                <div key={userId} className="mb-4">
                  <h6 className="text-white mb-2 border-bottom border-secondary pb-1">
                    User #{userId} ({entries.length} entries)
                  </h6>
                  <BaseTable
                    columns={perUserColumns.filter((col) => col.accessorKey !== 'userId')}
                    rows={entries}
                    onRowClick={handleRowClick}
                    customStyles={{
                      header: {
                        width: 'min-content',
                      },
                      cell: {
                        borderBottom: '1px solid #56577A',
                        cursor: 'pointer',
                        color: 'white',
                        width: 'min-content',
                      },
                      row: {
                        '&:hover': {
                          backgroundColor: '#2C373B',
                        },
                      },
                    }}
                  />
                </div>
              );
            })}
          </div>
        ) : (
          <BaseTable
            columns={overallColumns}
            rows={rows}
            onRowClick={handleRowClick}
            customStyles={{
              header: {
                width: 'min-content',
              },
              cell: {
                borderBottom: '1px solid #56577A',
                cursor: 'pointer',
                color: 'white',
                width: 'min-content',
              },
              row: {
                '&:hover': {
                  backgroundColor: '#2C373B',
                },
              },
            }}
          />
        )
      ) : (
        <p className="text-muted">No geolocation history available.</p>
      )}
    </div>
  );
};

